import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Divider,
  Container,
  Chip,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import AOS from 'aos';
import 'aos/dist/aos.css';

const SchoolDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolId } = location.state || {};

  const [school, setSchool] = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('students');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, easing: 'ease-in-out', once: true });
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/schools/admin/schools/${schoolId}`);
        setSchool(response.data.school);
        setStudents(response.data.students);
        setTeachers(response.data.teachers);
        setParents(response.data.parents);
        setLoading(false);
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to load school details');
        setLoading(false);
      }
    };

    if (schoolId) {
      fetchAllData();
    } else {
      setError('No school ID provided');
      setLoading(false);
    }
  }, [schoolId]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredData = (data) => {
    if (!searchQuery) return sortedData(data);
    return sortedData(data).filter((item) =>
      Object.values(item).some(
        (value) =>
          value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  };

  const handleDeleteSchool = async () => {
    if (adminEmail !== 'xe.fusion.xe@gmail.com') {
      setError('Please enter the correct admin email');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await api.delete(`/api/schools/admin/schools/${schoolId}`, {
        data: { adminEmail },
      });

      if (response.data.success) {
        navigate('/admin-dashboard', { state: { message: response.data.message } });
      } else {
        setError(response.data.message || 'Failed to delete school');
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to delete school'
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const renderStudentItem = (item) => (
    <Grid item xs={12} sm={6} md={4} key={item._id}>
      <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 1 }}>
            {item.fullName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
            Admission: {item.admissionNumber}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Class: {item.class}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderTeacherItem = (item) => (
    <Grid item xs={12} sm={6} md={4} key={item._id}>
      <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 1 }}>
            {item.fullName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
            Email: {item.email}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Subjects:{' '}
            {Array.isArray(item.subjectSpecialization)
              ? item.subjectSpecialization.join(', ')
              : item.subjectSpecialization || 'Not specified'}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderParentItem = (item) => (
    <Grid item xs={12} sm={6} md={4} key={item._id}>
      <Card sx={{ boxShadow: 2, borderRadius: 2, p: 2, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 1 }}>
            {item.parentName}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
            Email: {item.email}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Child: {item.learnerId?.fullName || 'Not specified'}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderManageContent = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" sx={{ color: '#d32f2f', mb: 3, lineHeight: 1.6 }}>
        Warning: Deleting this school will permanently remove all its data including students,
        teachers, and parents.
      </Typography>
      <Button
        variant="contained"
        color="error"
        onClick={() => setShowDeleteModal(true)}
        sx={{ borderRadius: 1, px: 4, py: 1.5 }}
      >
        Delete School
      </Button>
    </Box>
  );

  const renderDeleteModal = () => (
    <Modal
      open={showDeleteModal}
      onClose={() => setShowDeleteModal(false)}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        sx={{
          bgcolor: 'white',
          p: 4,
          borderRadius: 2,
          width: { xs: '90%', sm: 400 },
          boxShadow: 24,
        }}
      >
        <Typography variant="h6" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 2 }}>
          Confirm Deletion
        </Typography>
        <Typography sx={{ mb: 2, lineHeight: 1.5 }}>
          This action cannot be undone. All school data will be permanently deleted.
        </Typography>
        <Typography sx={{ mb: 1 }}>
          To confirm, please enter the admin email:
        </Typography>
        <TextField
          fullWidth
          placeholder="xe.fusion.xe@gmail.com"
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          sx={{ mb: 3 }}
          autoComplete="off"
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowDeleteModal(false)}
            sx={{ borderColor: '#ccc', color: '#666' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSchool}
            disabled={isDeleting}
            sx={{ borderRadius: 1 }}
          >
            {isDeleting ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );

  const renderHeader = () => (
    <Box
      sx={{
        bgcolor: 'white',
        p: 3,
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      }}
      data-aos="fade-down"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackIcon sx={{ color: '#9C27B0' }} />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          {school?.schoolName}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
        Code: {school?.schoolCode}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <EmailIcon sx={{ color: '#666', mr: 1, fontSize: 18 }} />
        <Typography variant="body2" sx={{ color: '#666' }}>
          {school?.email}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <PhoneIcon sx={{ color: '#666', mr: 1, fontSize: 18 }} />
        <Typography variant="body2" sx={{ color: '#666' }}>
          {school?.phoneNumber}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LocationOnIcon sx={{ color: '#666', mr: 1, fontSize: 18 }} />
        <Typography variant="body2" sx={{ color: '#666' }}>
          {school?.address}, {school?.location}, {school?.village}, {school?.subcounty},{' '}
          {school?.county}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Chip
          label={school?.isActive ? 'Active' : 'Inactive'}
          sx={{
            bgcolor: school?.isActive ? '#e8f5e9' : '#ffebee',
            color: school?.isActive ? '#2e7d32' : '#c62828',
            fontWeight: 'bold',
          }}
        />
        <Typography variant="caption" sx={{ color: '#999' }}>
          Registered:{' '}
          {school?.createdAt ? new Date(school.createdAt).toLocaleDateString() : 'N/A'}
        </Typography>
      </Box>
    </Box>
  );

  const renderSearchBar = () => (
    <TextField
      fullWidth
      label={`Search ${activeTab}...`}
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      InputProps={{
        endAdornment: <SearchIcon sx={{ color: '#666' }} />,
      }}
      sx={{ mb: 3, bgcolor: 'white', borderRadius: 1, boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
    />
  );

  const renderSortButtons = () => {
  const sortFields = {
    students: [
      { key: 'fullName', label: 'Name' },
      { key: 'admissionNumber', label: 'Admission' },
      { key: 'class', label: 'Class' },
    ],
    teachers: [
      { key: 'fullName', label: 'Name' },
      { key: 'email', label: 'Email' },
    ],
    parents: [
      { key: 'parentName', label: 'Name' },
      { key: 'email', label: 'Email' },
    ],
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
      {sortFields[activeTab]?.map((field) => (
        <Button
          key={field.key}
          variant={sortConfig.key === field.key ? 'contained' : 'outlined'}
          onClick={() => handleSort(field.key)}
          sx={{
            bgcolor: sortConfig.key === field.key ? '#9C27B0' : '#eee',
            color: sortConfig.key === field.key ? 'white' : '#666',
            '&:hover': { bgcolor: sortConfig.key === field.key ? '#7B1FA2' : '#ddd' },
            borderRadius: 1,
          }}
        >
          {field.label}{' '}
          {sortConfig.key === field.key && (sortConfig.direction === 'asc' ? '↑' : '↓')}
        </Button>
      ))}
    </Box>
  );
}; 
  

  const renderContent = () => {
    let data, renderItem, emptyText;

    switch (activeTab) {
      case 'students':
        data = filteredData(students);
        renderItem = renderStudentItem;
        emptyText = 'No students found';
        break;
      case 'teachers':
        data = filteredData(teachers);
        renderItem = renderTeacherItem;
        emptyText = 'No teachers found';
        break;
      case 'parents':
        data = filteredData(parents);
        renderItem = renderParentItem;
        emptyText = 'No parents found';
        break;
      case 'manage':
        return renderManageContent();
      default:
        return null;
    }

    return (
      <>
        {activeTab !== 'manage' && renderSearchBar()}
        {activeTab !== 'manage' && renderSortButtons()}
        {data.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: '#666', mt: 5 }}>
            {emptyText}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {data.map((item) => renderItem(item))}
          </Grid>
        )}
      </>
    );
  };

  const tabs = [
    { key: 'students', name: 'Students', count: students.length },
    { key: 'teachers', name: 'Teachers', count: teachers.length },
    { key: 'parents', name: 'Parents', count: parents.length },
    { key: 'manage', name: 'Manage' },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#9C27B0' }} />
        <Typography variant="h6" sx={{ mt: 3, color: '#2c3e50' }}>
          Loading school details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 3, textAlign: 'center' }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(-1)}
          sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' }, px: 4, py: 1.5 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      {renderHeader()}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{
          bgcolor: 'white',
          mb: 2,
          borderRadius: 2,
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        }}
        indicatorColor="primary"
        textColor="primary"
        data-aos="fade-up"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            label={`${tab.name} ${tab.count !== undefined ? `(${tab.count})` : ''}`}
            sx={{ textTransform: 'none', fontWeight: 'medium', color: '#2c3e50' }}
          />
        ))}
      </Tabs>
      <Box sx={{ bgcolor: 'white', p: 3, borderRadius: 2, boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }} data-aos="fade-up">
        {renderContent()}
      </Box>
      {renderDeleteModal()}
    </Container>
  );
};

export default SchoolDetails;